import React, { useMemo, useState } from 'react';
import { Box, Typography, Avatar, Divider } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ScienceIcon from '@mui/icons-material/Science';
import BugReportIcon from '@mui/icons-material/BugReport';
import GrassIcon from '@mui/icons-material/Grass';

// Search bar styling (same as SearchBar component)
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha('#222C2F', 0.8),
  '&:hover': {
    backgroundColor: alpha('#222C2F', 0.9),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: 0,
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: '#fff',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    fontSize: 14,
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
      '&:focus': {
        width: '25ch',
      },
    },
  },
}));

// Event type icons and colors (from TimelineEvents)
const iconMap = {
  fertilizer: <LocalFloristIcon sx={{ fontSize: 18 }} />, // slightly smaller
  fungicide: <ScienceIcon sx={{ fontSize: 18 }} />,
  insecticide: <BugReportIcon sx={{ fontSize: 18 }} />,
  herbicide: <GrassIcon sx={{ fontSize: 18 }} />,
};
const colorMap = {
  fertilizer: '#82ca9d',
  fungicide: '#8884d8',
  insecticide: '#ffc658',
  herbicide: '#d32f2f',
};

const aggregateEventUsage = (events, unitsPlanted) => {
  const aggregatedData = {};
  ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].forEach((eventTypeKey) => {
    (events[eventTypeKey] || []).forEach((record) => {
      const eventType = eventTypeKey.replace('Events', '');
      const productNameRaw = record[`${eventType}_name`] || 'N/A';
      const productNameKey = productNameRaw.trim().toLowerCase();
      const unitType = record.amt_used_unit_type || 'N/A';
      const amtUsed = record.amt_used || 0;
      if (!aggregatedData[eventType]) aggregatedData[eventType] = {};
      if (!aggregatedData[eventType][productNameKey]) {
        aggregatedData[eventType][productNameKey] = {
          eventType,
          productName: productNameRaw, // keep original case from first occurrence
          amtUsed: 0,
          unitType,
          perPlant: 0,
        };
      }
      aggregatedData[eventType][productNameKey].amtUsed += amtUsed;
    });
  });
  // Calculate per-plant usage
  const rows = [];
  Object.values(aggregatedData).forEach(typeGroup => {
    Object.values(typeGroup).forEach(row => {
      row.perPlant = unitsPlanted ? (row.amtUsed / unitsPlanted).toFixed(4) : 'N/A';
      rows.push(row);
    });
  });
  return rows;
};

const EventUsageSummary = ({ events = {}, unitsPlanted = 1 }) => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const rows = useMemo(() => {
    const allRows = aggregateEventUsage(events, unitsPlanted);
    if (!searchQuery.trim()) return allRows;
    
    const query = searchQuery.toLowerCase();
    return allRows.filter(row => 
      row.eventType.toLowerCase().includes(query) ||
      row.productName.toLowerCase().includes(query) ||
      row.unitType.toLowerCase().includes(query)
    );
  }, [events, unitsPlanted, searchQuery]);

  return (
    <Box sx={{
      bgcolor: 'transparent',
      color: '#fff',
      borderRadius: 1.5,
      px: 2,
      pt: 0,
      pb: 3, // Increased bottom padding slightly
      mt: 0,
      minHeight: 240,
      width: '100%',
    }}>
      <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 500, fontSize: 15, mb: 1 }}>
        Event Usage Summary
      </Typography>
      
      {/* Search Bar */}
      <Box sx={{ width: '100%', padding: '8px 0', mb: .5}} onClick={(e) => e.stopPropagation()}>
        <Search>
          <SearchIconWrapper>
            <SearchIcon sx={{ color: '#AAB4C8', fontSize: '18px' }} />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search events..."
            inputProps={{ 'aria-label': 'search' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
          />
        </Search>
      </Box>
      
      {rows.length === 0 ? (
        <Typography sx={{ color: '#AAB4C8', fontSize: 14 }}>
          {searchQuery ? 'No events match your search.' : 'No usage events found.'}
        </Typography>
      ) : (
        <Box component="ul" sx={{ 
          listStyle: 'none', 
          m: 0, 
          width: '100%',
          maxHeight: 300,
          paddingLeft: 0,
          overflowY: 'auto', 
          cursor: 'default', 
          pointerEvents: 'auto',
          '& > :last-child': {
            marginBottom: 3 // Add padding at the bottom of the last item
          }
        }} onClick={e => e.stopPropagation()}>
          {rows.map((row, idx) => (
            <React.Fragment key={idx}>
              <Box component="li" sx={{ display: 'flex', alignItems: 'flex-start', py: 0.75, px: 0 }}>
                <Avatar sx={{ 
                  bgcolor: colorMap[row.eventType] || '#bdbdbd', 
                  color: '#181F2A', 
                  width: 24, 
                  height: 24, 
                  fontSize: 16, 
                  mr: 1 
                }}>
                  {iconMap[row.eventType]}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0, mt: -0.25 }}>
                  <Typography sx={{ 
                    color: '#fff', 
                    fontWeight: 500, 
                    fontSize: 13, 
                    lineHeight: 1.2,
                    mb: 0.1
                  }}>
                    {row.eventType.charAt(0).toUpperCase() + row.eventType.slice(1)}
                  </Typography>
                  <Typography sx={{ 
                    color: '#AAB4C8', 
                    fontSize: 12, 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    lineHeight: 1.2
                  }}>
                    {row.productName}
                  </Typography>
                </Box>
                <Box sx={{ 
                  minWidth: 80, 
                  textAlign: 'right', 
                  pr: 0, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-end',
                  mt: -0.25
                }}>
                  <Typography sx={{ 
                    color: '#fff', 
                    fontWeight: 500, 
                    fontSize: 13, 
                    mb: 0.1,
                    lineHeight: 1.2
                  }}>
                    {row.amtUsed} {row.unitType}
                  </Typography>
                  <Typography sx={{ 
                    color: '#AAB4C8', 
                    fontSize: 11,
                    lineHeight: 1.2
                  }}>
                    per plant: {row.perPlant} {row.unitType}
                  </Typography>
                </Box>
              </Box>
              {idx < rows.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', ml: 0, my: 0.25 }} />}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default EventUsageSummary;